import { format } from "date-fns";
import { hr } from "date-fns/locale";
import { Calendar as CalendarIcon, Clock } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));

const parseValue = (value) => {
  if (!value) return { date: undefined, h: "08", m: "00" };
  const [datePart, timePart] = value.split("T");
  const [y, mo, d] = datePart.split("-").map(Number);
  const [h = "08", m = "00"] = (timePart || "08:00").split(":");
  return { date: new Date(y, mo - 1, d), h, m };
};

const toValue = (date, h, m) => {
  if (!date) return "";
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${mo}-${d}T${h}:${m}`;
};

// Hrvatski odabir datuma i vremena: format dd.MM.yyyy, sati 0-23 (bez AM/PM).
export const DateTimePicker = ({ value, onChange, testid }) => {
  const { date, h, m } = parseValue(value);

  const label = value
    ? `${format(date, "dd.MM.yyyy.", { locale: hr })} ${h}:${m}`
    : "Odaberite datum i vrijeme";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          data-testid={testid}
          className={`w-full h-12 justify-start bg-white border-slate-200 font-normal text-base hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-blue-600/20 focus-visible:border-blue-600 ${
            value ? "text-slate-900" : "text-slate-400"
          }`}
        >
          <CalendarIcon size={18} className="mr-2 text-slate-400" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(d) => onChange(toValue(d, h, m))}
          locale={hr}
          weekStartsOn={1}
          initialFocus
        />
        <div className="flex items-center gap-2 border-t border-slate-200 p-3">
          <Clock size={16} className="text-slate-400 shrink-0" />
          <span className="text-sm text-slate-600 mr-1">Vrijeme</span>
          <Select value={h} onValueChange={(nh) => onChange(toValue(date || new Date(), nh, m))}>
            <SelectTrigger className="h-9 w-[72px]" data-testid={`${testid}-hour`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {HOURS.map((hh) => (
                <SelectItem key={hh} value={hh}>
                  {hh}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="text-slate-500">:</span>
          <Select value={m} onValueChange={(nm) => onChange(toValue(date || new Date(), h, nm))}>
            <SelectTrigger className="h-9 w-[72px]" data-testid={`${testid}-minute`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {MINUTES.map((mm) => (
                <SelectItem key={mm} value={mm}>
                  {mm}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </PopoverContent>
    </Popover>
  );
};
